import { useEffect, useState } from "react";
import { adminApi, formatAud } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Download, Package2, Link2, Copy, ExternalLink, RefreshCcw, Check,
  Plus, X, Trash2, Search, MessageSquare, Phone, AlertTriangle,
  ShoppingCart, Store, Globe, Building2, Truck,
  Shield, ShieldOff, Clock, FileText, Undo2, Banknote,
  AlertOctagon, Mail, Send, CheckCircle2,
} from "lucide-react";
import SyncBadge from "../../components/SyncBadge";
import { toast } from "sonner";

/* ─── Badges ─────────────────────────────────────────────────────── */
const SOURCE_BADGE = {
  website:  "badge badge-blue",
  walkin:   "badge badge-green",
  phone:    "badge badge-amber",
  ebay:     "badge badge-pink",
  trade:    "badge badge-purple",
  pinnacle: "badge badge-orange",
};

const STATUSES = ["new","confirmed","packed","shipped","invoiced","cancelled","stock_hold"];

const STATUS_STYLE = {
  new:        { background:"#fef3c7", color:"#92400e",  border:"1px solid #fde68a" },
  confirmed:  { background:"#dbeafe", color:"#1e40af",  border:"1px solid #93c5fd" },
  packed:     { background:"#e0e7ff", color:"#3730a3",  border:"1px solid #a5b4fc" },
  shipped:    { background:"#ccfbf1", color:"#0f766e",  border:"1px solid #5eead4" },
  invoiced:   { background:"#dcfce7", color:"#15803d",  border:"1px solid #86efac" },
  cancelled:  { background:"#fee2e2", color:"#991b1b",  border:"1px solid #fca5a5" },
  stock_hold: { background:"#ffedd5", color:"#9a3412",  border:"1px solid #fdba74" },
  paid:       { background:"#dcfce7", color:"#15803d",  border:"1px solid #86efac" },
  forfeited:  { background:"#fee2e2", color:"#991b1b",  border:"1px solid #fca5a5" },
};

const PAY_STYLE = {
  paid:        { background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" },
  cod_pending: { background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a" },
  pending:     { background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a" },
  refunded:    { background:"#ede9fe", color:"#5b21b6", border:"1px solid #c4b5fd" },
  failed:      { background:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5" },
  forfeited:   { background:"#fee2e2", color:"#7f1d1d", border:"1px solid #fca5a5" },
};

const SOURCE_ICON = { website:Globe, walkin:Store, phone:Phone, ebay:ShoppingCart, trade:Building2, pinnacle:RefreshCcw };

const daysSince  = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
const daysUntil  = (iso) => Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
const fmtDate    = (iso) => new Date(iso).toLocaleDateString("en-AU");
const collectByDate = () => { const d=new Date(); d.setDate(d.getDate()+7); return d.toLocaleDateString("en-AU",{day:"numeric",month:"long",year:"numeric"}); };

const CARRIERS = [
  "Australia Post",
  "Sendle",
  "StarTrack",
  "TNT / FedEx",
  "DHL Express",
  "Couriers Please",
  "Aramex",
  "Hunter Express",
  "Toll",
  "BCP Own Truck (Local Delivery)",
  "Customer Pickup",
  "Other",
];

function ShippingModal({ order, onClose, onConfirm }) {
  const isLocalOrPickup = (c) =>
    c === "BCP Own Truck (Local Delivery)" || c === "Customer Pickup";

  const [carrier,        setCarrier]        = useState("Australia Post");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [note,           setNote]           = useState("");
  const [submitting,     setSubmitting]     = useState(false);

  const needsTracking = !isLocalOrPickup(carrier);

  const submit = async () => {
    if (needsTracking && !trackingNumber.trim()) {
      toast.error("Please enter a tracking number for this carrier");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({
        carrier,
        tracking_number: needsTracking ? trackingNumber.trim() : "",
        note: note.trim() || `Shipped via ${carrier}${trackingNumber ? ` — tracking: ${trackingNumber}` : ""}`,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4"
        style={{ border: "1px solid #dde3ef" }}
        onClick={e => e.stopPropagation()}>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "#94a3b8" }}>
            Mark as Shipped
          </div>
          <div className="font-bold text-lg" style={{ color: "#0b1a3b" }}>
            {order.order_number}
          </div>
          <div className="text-sm" style={{ color: "#64748b" }}>
            {order.customer_name} · {order.fulfillment_type === "click_collect" ? "Click & Collect" : "Delivery"}
          </div>
        </div>

        <div>
          <label className="text-[11px] font-mono uppercase tracking-wide block mb-1" style={{ color: "#64748b" }}>
            Carrier / Shipping Method
          </label>
          <select value={carrier} onChange={e => { setCarrier(e.target.value); setTrackingNumber(""); }}
            className="w-full rounded-xl border px-3 py-2 text-sm"
            style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}>
            {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {needsTracking && (
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wide block mb-1" style={{ color: "#64748b" }}>
              Tracking Number <span style={{ color: "#e31e24" }}>*</span>
            </label>
            <input
              value={trackingNumber}
              onChange={e => setTrackingNumber(e.target.value)}
              placeholder="e.g. 7J9D3748576AUS"
              className="w-full rounded-xl border px-3 py-2 text-sm font-mono"
              style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}
            />
            <div className="text-[10px] mt-1" style={{ color: "#94a3b8" }}>
              This will appear on the customer's shipping email and invoice.
            </div>
          </div>
        )}

        {!needsTracking && (
          <div className="rounded-xl p-3 text-xs" style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#15803d" }}>
            ✅ No tracking number needed for {carrier} — customer will be notified.
          </div>
        )}

        <div>
          <label className="text-[11px] font-mono uppercase tracking-wide block mb-1" style={{ color: "#64748b" }}>
            Note to customer (optional)
          </label>
          <input
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. Fragile — handle with care"
            className="w-full rounded-xl border px-3 py-2 text-sm"
            style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}
          />
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-xl text-sm font-bold"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            Cancel
          </button>
          <button onClick={submit} disabled={submitting}
            className="flex-1 py-2 rounded-xl text-sm font-bold disabled:opacity-50"
            style={{ background: "#0b1a3b", color: "#ffffff" }}>
            {submitting ? "Marking shipped…" : "Confirm Shipped"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InvoiceActions({ order, onDone, adminApi, toast }) {
  const [invoicing,     setInvoicing]     = useState(false);
  const [sellerNote,    setSellerNote]    = useState(order.invoice_seller_note || "");
  const [showMissing,   setShowMissing]   = useState(false);
  const [missingFields, setMissingFields] = useState(() => {
    const missingSkus = (order.items || []).filter(i => i.sku && !i.donor_vin && !i.yard_location).map(i => i.sku);
    const masterSku = missingSkus[0];
    const map = {};
    (order.items || []).forEach(item => {
      if (item.sku) map[item.sku] = { vin: "", yard_location: "", same: item.sku !== masterSku };
    });
    return map;
  });

  const isWalkinOrPhone = ["walkin", "phone"].includes(order.source);
  const isShipped  = ["shipped", "invoiced"].includes(order.status) || isWalkinOrPhone;
  const hasInvoice = !!order.invoice_number;

  // Items that have no VIN and no yard location — Pinnacle doesn't supply these.
  // Prefer the enriched invoice_items (has whatever VIN was already saved at
  // invoice time) over the bare order.items, which never carries donor_vin at all.
  const sourceItems = (order.invoice_items && order.invoice_items.length) ? order.invoice_items : (order.items || []);
  const itemsMissingDetails = sourceItems.filter(i => i.sku && !i.donor_vin && !i.yard_location);
  // Allowed both before and after invoicing — VIN/location often only becomes
  // known after the invoice already went out, and the invoice can be updated.
  const hasMissingDetails = itemsMissingDetails.length > 0;

  const missingSkus = itemsMissingDetails.map(i => i.sku);
  const masterSku    = missingSkus[0];

  // Editing the first ("master") part cascades VIN + yard location to every
  // other part still marked "same donor vehicle" — parts that were unlinked
  // (a different donor vehicle) are left untouched.
  const handleMissingChange = (sku, field, value) =>
    setMissingFields(prev => {
      const next = { ...prev, [sku]: { ...prev[sku], [field]: value } };
      if (sku === masterSku) {
        missingSkus.forEach(s => {
          if (s !== masterSku && next[s]?.same) {
            next[s] = { ...next[s], [field]: value };
          }
        });
      }
      return next;
    });

  // Toggle a non-master part between "same donor vehicle" (mirrors master)
  // and "different donor vehicle" (independently editable).
  const toggleSame = (sku) =>
    setMissingFields(prev => {
      const turningOn = !prev[sku]?.same;
      return {
        ...prev,
        [sku]: {
          ...prev[sku],
          same: turningOn,
          vin: turningOn ? (prev[masterSku]?.vin || "") : prev[sku]?.vin,
          yard_location: turningOn ? (prev[masterSku]?.yard_location || "") : prev[sku]?.yard_location,
        },
      };
    });

  const generateInvoice = async () => {
    setInvoicing(true);
    try {
      // Save any manually entered VIN/location back to the product record
      for (const [sku, vals] of Object.entries(missingFields)) {
        if (vals.vin?.trim() || vals.yard_location?.trim()) {
          await adminApi.patch(
            `/admin/products/by-sku/${encodeURIComponent(sku)}/details`,
            { donor_vin: vals.vin?.trim() || undefined, yard_location: vals.yard_location?.trim() || undefined }
          ).catch(() => {}); // non-blocking
        }
      }
      await adminApi.post(`/admin/orders/${order.id}/generate-invoice`, {
        seller_note:    sellerNote.trim() || null,
        manual_details: missingFields,
      });
      if (hasInvoice) {
        toast.success("VIN / yard location updated — View & Print to see the corrected invoice");
      } else {
        toast.success(`Invoice generated — email sent to ${order.customer_email}`);
      }
      onDone();
    } catch(e) {
      toast.error(e?.response?.data?.detail || "Failed to generate invoice");
    } finally { setInvoicing(false); }
  };

  const viewInvoice = async () => {
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Popup blocked — please allow popups for this site in your browser settings");
      return;
    }
    win.document.write("<html><body style='font-family:Arial;padding:40px;color:#555'>Loading invoice...</body></html>");
    try {
      const token = localStorage.getItem("bcp_admin_token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
      const res = await fetch(`${backendUrl}/api/admin/orders/${order.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      win.document.write(html);
      win.document.close();
    } catch(e) {
      win.document.body.innerHTML = "<div style='font-family:Arial;padding:40px;color:red'>Failed to load invoice: " + e.message + "</div>";
    }
  };

  const resendInvoice = async () => {
    try {
      await adminApi.post(`/admin/orders/${order.id}/send-invoice`);
      toast.success(`Invoice resent to ${order.customer_email}`);
    } catch(e) { toast.error("Failed to resend"); }
  };

  if (order.payment_status !== "paid") return null;

  return (
    <div className="space-y-3 pt-1">

      {/* Seller note */}
      <div>
        <label className="text-[10px] font-mono uppercase tracking-wide block mb-1" style={{color:"#64748b"}}>
          Seller Note <span style={{color:"#94a3b8"}}>(prints on invoice)</span>
        </label>
        <textarea value={sellerNote} onChange={e => setSellerNote(e.target.value)}
          rows={2} disabled={hasInvoice}
          placeholder="e.g. Part tested and working. 6 month warranty applies."
          className="w-full rounded-xl border px-3 py-2 text-xs resize-none"
          style={{background:"#ffffff", borderColor:"#dde3ef", color:"#0f172a"}}/>
      </div>

      {/* Missing fields panel — collapsible, non-blocking */}
      {hasMissingDetails && (
        <div className="rounded-xl overflow-hidden" style={{border:"1px solid #fde68a"}}>
          <button onClick={() => setShowMissing(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2"
            style={{background:"#fffbeb"}}>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" style={{color:"#d97706"}}/>
              <span className="text-[11px] font-semibold" style={{color:"#92400e"}}>
                VIN &amp; yard location not available for {itemsMissingDetails.length} part{itemsMissingDetails.length > 1 ? "s" : ""}
              </span>
              <span className="text-[10px]" style={{color:"#b45309"}}>— add if known</span>
            </div>
            <span className="text-[10px] font-mono" style={{color:"#92400e"}}>
              {showMissing ? "▲ Hide" : "▼ Add details"}
            </span>
          </button>

          {showMissing && (
            <div className="px-3 pb-3 pt-2 space-y-3" style={{background:"#fffdf0"}}>
              {itemsMissingDetails.map((item, idx) => {
                const isMaster = item.sku === masterSku;
                const fieldVals = missingFields[item.sku] || {};
                const linked = !isMaster && fieldVals.same;
                return (
                  <div key={item.sku}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[10px] font-mono font-bold uppercase" style={{color:"#78716c"}}>
                        {item.name || item.sku}
                        <span className="ml-1 font-normal opacity-60">({item.sku})</span>
                      </div>
                      {!isMaster && (
                        <label className="flex items-center gap-1.5 text-[9px] font-mono cursor-pointer" style={{color:"#92400e"}}>
                          <input type="checkbox" checked={fieldVals.same}
                            onChange={() => toggleSame(item.sku)} />
                          Same donor vehicle as above
                        </label>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[9px] font-mono uppercase tracking-wide block mb-0.5" style={{color:"#94a3b8"}}>VIN (17 chars)</label>
                        <input
                          value={fieldVals.vin || ""}
                          onChange={e => handleMissingChange(item.sku, "vin", e.target.value.toUpperCase())}
                          placeholder="e.g. JM1BK32321234567"
                          maxLength={17}
                          disabled={linked}
                          className="w-full rounded-lg border px-2 py-1.5 text-[11px] font-mono disabled:opacity-60"
                          style={{background: linked ? "#f5f5f4" : "#ffffff", borderColor:"#fde68a", color:"#0f172a"}}/>
                      </div>
                      <div>
                        <label className="text-[9px] font-mono uppercase tracking-wide block mb-0.5" style={{color:"#94a3b8"}}>Yard Location</label>
                        <input
                          value={fieldVals.yard_location || ""}
                          onChange={e => handleMissingChange(item.sku, "yard_location", e.target.value)}
                          placeholder="e.g. Row 3, Bay 12"
                          disabled={linked}
                          className="w-full rounded-lg border px-2 py-1.5 text-[11px] disabled:opacity-60"
                          style={{background: linked ? "#f5f5f4" : "#ffffff", borderColor:"#fde68a", color:"#0f172a"}}/>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="text-[10px] pt-1" style={{color:"#92400e"}}>
                ✓ Fill in the first part's VIN/location and it copies to the rest automatically — untick "Same donor vehicle" on any part that's actually different. Values are saved to each part's record{hasInvoice ? " — click \"Save & Update Invoice\" below to apply it to the printed invoice." : " and printed on the invoice. You can skip and generate without them."}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-2 flex-wrap">
        {!hasInvoice ? (
          <button onClick={generateInvoice} disabled={!isShipped || invoicing}
            title={!isShipped ? "Ship the order first before generating an invoice" : ""}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{background: isShipped?"#0b1a3b":"#f5f7fb", color: isShipped?"#ffffff":"#94a3b8", border: isShipped?"none":"1px solid #dde3ef"}}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            {invoicing ? "Generating…" : isShipped ? "Generate Invoice" : "Ship first to invoice"}
          </button>
        ) : (
          <>
            <button onClick={viewInvoice}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
              style={{background:"#dcfce7", border:"1px solid #86efac", color:"#15803d"}}>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
              </svg>
              View &amp; Print — {order.invoice_number}
            </button>
            <button onClick={resendInvoice}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold"
              style={{background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569"}}>
              Resend Email
            </button>
            {hasMissingDetails && (
              <button onClick={generateInvoice} disabled={invoicing}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold disabled:opacity-50"
                style={{background:"#fffbeb", border:"1px solid #fde68a", color:"#92400e"}}>
                {invoicing ? "Saving…" : "Save & Update Invoice"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminOrders() {
  const [items,         setItems]         = useState([]);
  const [filter,        setFilter]        = useState("all");
  const [search,        setSearch]        = useState("");
  const [sourceFilter,  setSourceFilter]  = useState("all");
  const [timeFilter,    setTimeFilter]    = useState("today");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [manualOpen,    setManualOpen]    = useState(false);
  const [shippingModal, setShippingModal] = useState(null);
  const [refundModal, setRefundModal] = useState(null);       // order being cancelled — refund now/later choice
  const [completeRefundModal, setCompleteRefundModal] = useState(null); // order with a pending refund being completed

  // ── Time filter helpers ────────────────────────────────────────────
  const getDateRange = (tf) => {
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const pad   = (d) => d.toISOString();
    switch(tf) {
      case "today":
        return [pad(today), null];
      case "yesterday": {
        const yd = new Date(today); yd.setDate(yd.getDate()-1);
        return [pad(yd), pad(today)];
      }
      case "this_week": {
        const dow = today.getDay();
        const mon = new Date(today); mon.setDate(mon.getDate() - (dow===0?6:dow-1));
        return [pad(mon), null];
      }
      case "last_week": {
        const dow = today.getDay();
        const thisMon = new Date(today); thisMon.setDate(thisMon.getDate() - (dow===0?6:dow-1));
        const lastMon = new Date(thisMon); lastMon.setDate(lastMon.getDate()-7);
        return [pad(lastMon), pad(thisMon)];
      }
      case "this_month": {
        const fm = new Date(now.getFullYear(), now.getMonth(), 1);
        return [pad(fm), null];
      }
      case "last_month": {
        const fm  = new Date(now.getFullYear(), now.getMonth(), 1);
        const flm = new Date(now.getFullYear(), now.getMonth()-1, 1);
        return [pad(flm), pad(fm)];
      }
      default: return [null, null];
    }
  };

  const load = async () => {
    const [dateFrom, dateTo] = getDateRange(timeFilter);
    const params = new URLSearchParams();
    if (filter !== "all" && filter !== "everything") params.set("status", filter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo)   params.set("date_to",   dateTo);
    params.set("page_size", "200");
    const { data } = await adminApi.get(`/admin/orders?${params}`);
    setItems(data.items || []);
  };

  useEffect(() => { load(); }, [filter, timeFilter]); // eslint-disable-line

  const visible = items.filter((o) => {
    // Once invoiced, an order is a completed transaction — it lives in the
    // Invoices tab from here on, not cluttering the default Orders view.
    // Still reachable via the explicit "invoiced" filter toggle below.
    if (filter === "all" && o.status === "invoiced") return false;
    if (filter === "all" && o.status === "cancelled") return false;
    if (sourceFilter !== "all" && (o.source || "website") !== sourceFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(q) ||
      o.customer_name?.toLowerCase().includes(q) ||
      o.customer_email?.toLowerCase().includes(q) ||
      o.items?.some(i => i.name?.toLowerCase().includes(q) || i.sku?.toLowerCase().includes(q)) ||
      (o.source || "website").toLowerCase().includes(q)
    );
  });

  // Status counts — exclude invoiced (it's "done", not actionable)
  const ACTION_STATUSES = ["new","confirmed","packed","shipped","stock_hold"];
  const statusCounts = STATUSES.reduce((a,s) => { a[s]=items.filter(o=>o.status===s).length; return a; }, {});
  const actionCount  = ACTION_STATUSES.reduce((sum,s) => sum + (statusCounts[s]||0), 0);
  const totalRevenue = visible.reduce((s,o) => s+(o.total||0), 0);
  const sources      = [...new Set(items.map(o=>o.source||"website"))];
  const pendingCC    = items.filter(o=>o.fulfillment_type==="click_collect"&&!["cancelled","invoiced"].includes(o.status)).length;

  // ══════════════════════════════════════════════════════════════════
  // ALL PINNACLE / SYNC FUNCTIONS — 100% UNTOUCHED
  // ══════════════════════════════════════════════════════════════════
  const updateStatus = async (o, s, extra = {}) => {
    if (s === "shipped" && !extra.carrier) { setShippingModal(o); return; }
    if (s === "cancelled" && o.payment_status === "paid") {
      // This order was actually paid — don't just cancel silently, ask what
      // should happen to the money before doing anything.
      setRefundModal(o);
      return;
    }
    if (s === "invoiced") {
      // Staff invoice on Pinnacle's side directly — this dropdown option is
      // just the trigger to register that here too, in one step: assigns a
      // real invoice number, so the order shows correctly in the Invoices
      // tab straight away, with no separate "Generate Invoice" click needed.
      try {
        await adminApi.post(`/admin/orders/${o.id}/generate-invoice`, {});
        toast.success(`Order ${o.order_number} invoiced`);
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not invoice this order");
      }
      return;
    }
    await adminApi.put(`/admin/orders/${o.id}/status`, {
      new_status: s, note: extra.note || "",
      carrier: extra.carrier || null, tracking_number: extra.tracking_number || null,
    });
    toast.success(`Order ${o.order_number} → ${s}`);
    load();
  };
  const generateInvoiceQuick = async (o) => {
    try {
      await adminApi.post(`/admin/orders/${o.id}/generate-invoice`, {});
      toast.success(`Invoice generated for ${o.order_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate invoice");
    }
  };
  const revokeForfeiture = async (o) => {
    if (!window.confirm(`Revoke forfeiture on ${o.order_number}? This reinstates the order for normal processing — the customer's original payment stays applied, nothing gets charged again automatically.`)) return;
    try {
      await adminApi.post(`/admin/orders/${o.id}/revoke-forfeiture`, {});
      toast.success(`${o.order_number} reinstated — back in active orders`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke forfeiture");
    }
  };
  const [swapModal, setSwapModal] = useState(null); // order being swapped
  const [pinnaclePolling, setPinnaclePolling] = useState(false);
  const refreshPinnacleStatus = async () => {
    setPinnaclePolling(true);
    try {
      const { data } = await adminApi.post("/admin/pinnacle/poll-order-statuses", {});
      toast.success(`Checked ${data.polled || 0} orders — ${data.updated || 0} confirmed, ${data.flagged || 0} flagged`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check Pinnacle status");
    } finally { setPinnaclePolling(false); }
  };
  const notifyIssue = async (o) => {
    try {
      await adminApi.post(`/admin/orders/${o.id}/notify-issue`, {});
      toast.success(`Customer notified for ${o.order_number}`);
      load();
      if (selectedOrder?.id === o.id) setSelectedOrder(prev => ({ ...prev, customer_notified_at: new Date().toISOString() }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send notification");
    }
  };
  const pushToPinnacle = async (o) => {
    if (!window.confirm(`Push ${o.order_number} to Pinnacle now?`)) return;
    try {
      await adminApi.post(`/admin/orders/${o.id}/push-to-pinnacle`, {});
      toast.success(`${o.order_number} queued for Pinnacle`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not push to Pinnacle");
    }
  };
  const retrySync = async (o) => {
    try {
      const { data: qData } = await adminApi.get(`/admin/sync-queue?status=failed`);
      const event = (qData.items || []).find((e) => e.ref_id === o.id || e.payload?.order_id === o.id);
      if (event) { await adminApi.post(`/admin/sync-queue/${event.id}/retry`); toast.success(`Retry queued for ${o.order_number}`); }
      else { await adminApi.post(`/admin/orders/${o.id}/force-sync`); toast.success(`Force sync queued for ${o.order_number}`); }
      load();
    } catch (e) { toast.error("Retry failed: " + (e.response?.data?.detail || e.message)); }
  };
  const forceSync = async (o) => {
    try {
      const { data } = await adminApi.post(`/admin/orders/${o.id}/force-sync`, { confirmed: false });
      if (data.requires_confirmation) {
        const proceed = window.confirm(`⚠️ Duplicate Push Warning\n\nOrder ${o.order_number} is already linked to Pinnacle work order:\n"${data.external_id}"\n\n${data.warning}\n\nClick OK only if you are certain this order has NOT been received by Pinnacle.\nClicking OK may create a DUPLICATE work order in Pinnacle.`);
        if (!proceed) return;
        const { data: confirmed } = await adminApi.post(`/admin/orders/${o.id}/force-sync`, { confirmed: true });
        if (confirmed.ok) toast.success(`Force sync queued for ${o.order_number} (duplicate risk accepted)`);
        else toast.error("Force sync failed");
      } else if (data.ok) { toast.success(`Sync queued for ${o.order_number}`); }
      load();
    } catch (e) { toast.error("Force sync failed: " + (e.response?.data?.detail || e.message)); }
  };
  const exportPinnacle = async () => {
    try {
      const res = await adminApi.get("/admin/sync/orders/export?only_unexported=true", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a"); a.href = url;
      a.download = `bcp_orders_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Orders exported to Pinnacle CSV"); load();
    } catch { toast.error("Export failed"); }
  };
  // ══════════════════════════════════════════════════════════════════

  const TIME_FILTERS = [
    { key:"today",      label:"Today" },
    { key:"yesterday",  label:"Yesterday" },
    { key:"this_week",  label:"This Week" },
    { key:"last_week",  label:"Last Week" },
    { key:"this_month", label:"This Month" },
    { key:"last_month", label:"Last Month" },
    { key:"all",        label:"All" },
  ];

  // Navigate to AdminProducts and highlight a specific SKU
  const openInInventory = (sku) => {
    window.open(`/admin/products?q=${encodeURIComponent(sku)}&activeStatus=all`, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="admin-orders-page">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{color:"#94a3b8"}}>Order Book</div>
          <h1 className="font-heading text-[26px] font-bold" style={{color:"#0f172a"}}>Orders</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[12px]" style={{color:"#64748b"}}>
              {visible.length} order{visible.length!==1?"s":""} · {formatAud(totalRevenue)}
            </span>
            {actionCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold px-2 py-0.5 rounded-lg"
                style={{background:"#fef3c7",color:"#92400e",border:"1px solid #fde68a"}}>
                ⚡ {actionCount} need action
              </span>
            )}
            {pendingCC>0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold px-2 py-0.5 rounded-lg"
                style={{background:"#eff6ff",color:"#1e40af",border:"1px solid #93c5fd"}}>
                <Store className="h-3 w-3"/> {pendingCC} C&amp;C pending
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={()=>setManualOpen(true)}
            className="h-9 rounded-xl text-white font-semibold text-[13px]"
            style={{background:"#16a34a"}} data-testid="admin-orders-new-manual">
            <Plus className="h-4 w-4 mr-1.5"/> New Order
          </Button>
          <Button onClick={exportPinnacle}
            className="h-9 rounded-xl text-white font-semibold text-[13px]"
            style={{background:"#1e5bb8"}} data-testid="admin-orders-export">
            <Download className="h-4 w-4 mr-1.5"/> Export to Pinnacle
          </Button>
          <Button onClick={refreshPinnacleStatus} disabled={pinnaclePolling}
            className="h-9 rounded-xl font-semibold text-[13px]"
            style={{background:"#f5f7fb",border:"1px solid #dde3ef",color:"#475569"}}
            data-testid="admin-orders-refresh-pinnacle" title="Check Pinnacle right now for confirmed work order numbers">
            <RefreshCcw className={`h-4 w-4 mr-1.5 ${pinnaclePolling ? "animate-spin" : ""}`}/>
            {pinnaclePolling ? "Checking…" : "Refresh Pinnacle Status"}
          </Button>
        </div>
      </div>

      {/* Time filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {TIME_FILTERS.map(({key, label}) => {
          const count = key === "all" ? null : null; // counts come from loaded items already filtered
          return (
            <button key={key} onClick={() => setTimeFilter(key)}
              className="px-3 h-8 rounded-xl text-[11px] font-mono font-semibold uppercase tracking-wide transition-all"
              style={timeFilter===key
                ? {background:"#0b1a3b", color:"#ffffff"}
                : {background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b"}}>
              {label}
            </button>
          );
        })}
      </div>

      {/* Status + action count chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono uppercase tracking-widest" style={{color:"#94a3b8"}}>Status:</span>
        <button onClick={() => setFilter("all")}
          className="px-3 h-7 rounded-lg text-[11px] font-mono font-semibold uppercase tracking-wide transition-all"
          style={filter==="all" ? {background:"#0b1a3b",color:"#ffffff"} : {background:"#f5f7fb",border:"1px solid #dde3ef",color:"#64748b"}}>
          All {items.length > 0 && `(${items.length})`}
        </button>
        <button onClick={() => setFilter("everything")}
          className="px-3 h-7 rounded-lg text-[11px] font-mono font-semibold uppercase tracking-wide transition-all"
          style={filter==="everything" ? {background:"#0b1a3b",color:"#ffffff"} : {background:"#f5f7fb",border:"1px solid #dde3ef",color:"#64748b"}}
          title="Every order regardless of status — including invoiced and cancelled"
          data-testid="filter-everything">
          Everything
        </button>
        {ACTION_STATUSES.filter(s=>statusCounts[s]>0).map(s=>(
          <button key={s} onClick={()=>setFilter(filter===s?"all":s)}
            className="px-3 h-7 rounded-lg text-[11px] font-mono font-bold uppercase tracking-wide transition-all"
            style={filter===s
              ? {...STATUS_STYLE[s], boxShadow:`0 0 0 2px ${STATUS_STYLE[s]?.border?.replace("1px solid ","")}`}
              : {...STATUS_STYLE[s], opacity:0.8}}>
            {statusCounts[s]} {s.replace("_"," ")}
          </button>
        ))}
        {/* Invoiced shown separately as completed */}
        {statusCounts["invoiced"]>0 && (
          <button onClick={()=>setFilter(filter==="invoiced"?"all":"invoiced")}
            className="px-3 h-7 rounded-lg text-[11px] font-mono font-bold uppercase tracking-wide transition-all"
            style={filter==="invoiced"
              ? {...STATUS_STYLE["invoiced"], boxShadow:"0 0 0 2px #86efac"}
              : {background:"#f0fdf4",color:"#15803d",border:"1px solid #86efac",opacity:0.8}}>
            ✓ {statusCounts["invoiced"]} invoiced
          </button>
        )}
        {/* Cancelled also hidden from the default view, same treatment as invoiced */}
        {statusCounts["cancelled"]>0 && (
          <button onClick={()=>setFilter(filter==="cancelled"?"all":"cancelled")}
            className="px-3 h-7 rounded-lg text-[11px] font-mono font-bold uppercase tracking-wide transition-all"
            style={filter==="cancelled"
              ? {background:"#fef2f2",color:"#991b1b",border:"1px solid #fca5a5",boxShadow:"0 0 0 2px #fca5a5"}
              : {background:"#fef2f2",color:"#991b1b",border:"1px solid #fca5a5",opacity:0.8}}
            data-testid="filter-cancelled-toggle">
            ✕ {statusCounts["cancelled"]} cancelled
          </button>
        )}
        {filter!=="all" && (
          <button onClick={()=>setFilter("all")} className="text-[11px] font-mono hover:underline" style={{color:"#94a3b8"}}>
            Clear ×
          </button>
        )}
      </div>

      {/* Search + source filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{color:"#94a3b8"}}/>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Order #, customer, part name or SKU…"
            className="w-full pl-9 pr-4 h-9 rounded-xl text-[12px] outline-none"
            style={{background:"#ffffff",border:"1px solid #dde3ef",color:"#0f172a"}}/>
          {search && <button onClick={()=>setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2" style={{color:"#94a3b8"}}><X className="h-3 w-3"/></button>}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {["all",...sources].map(src => {
            const Icon = src==="all" ? null : SOURCE_ICON[src]||Globe;
            return (
              <button key={src} onClick={()=>setSourceFilter(sourceFilter===src?"all":src)}
                className="flex items-center gap-1 px-2.5 h-9 rounded-xl text-[11px] font-mono font-semibold uppercase tracking-wide"
                style={sourceFilter===src ? {background:"#1e5bb8",color:"#ffffff"} : {background:"#f5f7fb",border:"1px solid #dde3ef",color:"#64748b"}}>
                {Icon && <Icon className="h-3 w-3"/>} {src}
              </button>
            );
          })}
        </div>
      </div>

      {/* Orders — slim table */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm" style={{border:"1px solid #dde3ef"}}>
        <table className="w-full" style={{borderCollapse:"collapse"}}>
          <thead>
            <tr style={{background:"#f5f7fb",borderBottom:"1px solid #dde3ef"}}>
              {[
                ["Order",    "left",   120],
                ["Part",     "left",   190],
                ["Vehicle",  "left",   110],
                ["Customer", "left",   150],
                ["Payment",  "center", 100],
                ["Status",   "center", 130],
                ["Sync",     "center", 100],
                ["Pinnacle #","center",100],
                ["Total",    "right",   90],
                ["",         "right",   80],
              ].map(([h, align, w]) => (
                <th key={h} className="py-2.5 px-3 font-mono font-semibold uppercase"
                  style={{color:"#94a3b8",fontSize:10,letterSpacing:"0.12em",textAlign:align,width:w}}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((o, idx) => {
              const days      = daysSince(o.created_at);
              const aged      = days >= 7;
              const isCC      = o.fulfillment_type === "click_collect";
              const firstItem = o.items?.[0];
              const extraCount = (o.items?.length || 0) - 1;
              const rowBg = o.status === "cancelled" ? "#fef2f2" : (idx % 2 === 0 ? "#ffffff" : "#fafbfc");

              return (
                <tr key={o.id}
                  className="transition-colors hover:bg-blue-50 cursor-pointer"
                  style={{borderBottom:"1px solid #f0f4f8", background:rowBg}}
                  onClick={() => setSelectedOrder(o)}
                  data-testid={`admin-order-row-${o.order_number}`}>

                  {/* ORDER */}
                  <td className="px-3 py-2.5" onClick={e=>e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      {aged && <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"/>}
                      <div>
                        <button onClick={() => setSelectedOrder(o)}
                          className="font-heading font-bold text-[13px] hover:underline block text-left"
                          style={{color:"#0b1a3b"}}>
                          {o.order_number}
                        </button>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className={`${SOURCE_BADGE[o.source]||"badge badge-gray"}`}
                            style={{fontSize:9,padding:"1px 5px"}}>
                            {o.source||"web"}
                          </span>
                          {isCC && <Store className="h-2.5 w-2.5 text-blue-500"/>}
                          {o.insurance && <Shield className="h-2.5 w-2.5 text-green-500"/>}
                        </div>
                        <div className="text-[9px] font-mono mt-0.5" style={{color:"#94a3b8"}}>
                          {new Date(o.created_at).toLocaleString("en-AU",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                          {days > 0 && <span className={aged?"text-amber-500 font-bold":""}> · {days}d</span>}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* PART */}
                  <td className="px-3 py-2.5" onClick={e=>e.stopPropagation()}>
                    {firstItem ? (
                      <div>
                        <button onClick={() => openInInventory(firstItem.sku)}
                          className="text-[12px] font-medium hover:underline flex items-center gap-1 text-left"
                          style={{color:"#1e40af",maxWidth:190}}>
                          <span className="truncate">{firstItem.name || firstItem.sku}</span>
                          <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-50"/>
                        </button>
                        <div className="text-[9px] font-mono" style={{color:"#94a3b8"}}>{firstItem.sku}</div>
                        {extraCount > 0 && (
                          <span className="text-[9px] font-mono" style={{color:"#64748b"}}>+{extraCount} more</span>
                        )}
                      </div>
                    ) : <span style={{color:"#94a3b8"}}>—</span>}
                  </td>

                  {/* VEHICLE */}
                  <td className="px-3 py-2.5">
                    <div className="text-[11px] font-medium truncate" style={{color:"#334155",maxWidth:105}}>
                      {[o.first_item_vehicle_make, o.first_item_vehicle_model].filter(Boolean).join(" ") || <span style={{color:"#cbd5e1"}}>—</span>}
                    </div>
                  </td>

                  {/* CUSTOMER */}
                  <td className="px-3 py-2.5">
                    <div className="text-[12px] font-medium truncate" style={{color:"#0f172a",maxWidth:155}}>{o.customer_name}</div>
                    <div className="text-[10px] font-mono truncate" style={{color:"#64748b",maxWidth:155}}>{o.customer_email}</div>
                  </td>

                  {/* PAYMENT */}
                  <td className="px-3 py-2.5 text-center" onClick={e=>e.stopPropagation()}>
                    <div className="text-[9px] font-mono uppercase mb-0.5" style={{color:"#94a3b8"}}>
                      {(o.payment_method||"").replace(/_/g," ").split(" ")[0]}
                    </div>
                    <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded-md"
                      style={PAY_STYLE[(o.payment_status||"pending").toLowerCase()]||{}}>
                      {o.payment_status}
                    </span>
                  </td>

                  {/* STATUS */}
                  <td className="px-3 py-2.5 text-center" onClick={e=>e.stopPropagation()}>
                    <select value={o.status} onChange={e=>{e.stopPropagation();updateStatus(o,e.target.value);}}
                      onClick={e=>e.stopPropagation()}
                      className="h-6 px-1.5 pr-5 text-[9px] font-mono font-bold uppercase tracking-wide rounded-md cursor-pointer outline-none w-full"
                      style={{
                        ...(STATUS_STYLE[o.status]||{}),
                        WebkitAppearance:"none",appearance:"none",
                        backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                        backgroundRepeat:"no-repeat",backgroundPosition:"right 4px center",
                      }}
                      data-testid={`status-select-${o.order_number}`}>
                      {STATUSES.map(s=>(
                        <option key={s} value={s} style={{background:"#ffffff",color:"#0f172a",fontWeight:400}}>
                          {s.replace("_"," ")}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* SYNC */}
                  <td className="px-3 py-2.5 text-center" onClick={e=>e.stopPropagation()}>
                    <SyncBadge
                      status={o.sync_status||(o.exported_to_pinnacle?"synced":"pending")}
                      lastSyncedAt={o.last_synced_at}
                      syncError={o.sync_error}
                      onRetry={o.sync_status==="failed"||o.sync_status==="dead"?()=>retrySync(o):undefined}
                      onForceSync={(!o.sync_status||o.sync_status==="pending")?()=>forceSync(o):undefined}
                    />
                  </td>

                  {/* PINNACLE # */}
                  <td className="px-3 py-2.5 text-center">
                    {o.pinnacle_work_order ? (
                      <span className="text-[11px] font-mono font-bold" style={{color:"#15803d"}} title="Confirmed Pinnacle work order number">
                        #{o.pinnacle_work_order}
                      </span>
                    ) : o.pinnacle_issue ? (
                      <span className="text-[10px] font-mono font-bold uppercase" style={{color:"#991b1b"}}>Issue</span>
                    ) : o.sync_status === "synced" ? (
                      <span className="text-[10px] font-mono" style={{color:"#cbd5e1"}} title="Sent, waiting on Pinnacle to confirm">…pending</span>
                    ) : (
                      <span style={{color:"#cbd5e1"}}>—</span>
                    )}
                  </td>

                  {/* TOTAL */}
                  <td className="px-3 py-2.5 text-right">
                    <div className="font-heading font-bold text-[13px]" style={{color:"#0f172a"}}>{formatAud(o.total)}</div>
                    {o.insurance_amount>0 && (
                      <div className="text-[9px] font-mono" style={{color:"#16a34a"}}>+{formatAud(o.insurance_amount)}</div>
                    )}
                  </td>

                  {/* ACTIONS */}
                  <td className="px-3 py-2.5 text-right" onClick={e=>e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      {o.payment_status!=="paid" && o.payment_status!=="forfeited" && <PayLinkButton order={o} onUpdated={load}/>}
                      {!o.invoice_number && o.status!=="cancelled" && (
                        <Button size="sm" onClick={()=>generateInvoiceQuick(o)} className="rounded-lg text-[12px]"
                          style={{background:"#dbeafe",color:"#1e40af",border:"1px solid #93c5fd"}}
                          data-testid={`quick-invoice-${o.order_number}`} title="Generate invoice for this order">
                          <FileText className="h-3 w-3 mr-1"/>Invoice
                        </Button>
                      )}
                      {o.payment_status==="forfeited" && (
                        <Button size="sm" onClick={()=>revokeForfeiture(o)} className="rounded-lg text-[12px]"
                          style={{background:"#fef3c7",color:"#92400e",border:"1px solid #fcd34d"}}
                          data-testid={`revoke-forfeiture-${o.order_number}`} title="Reinstate this order for normal processing">
                          <Undo2 className="h-3 w-3 mr-1"/>Revoke
                        </Button>
                      )}
                      {o.refund_status==="pending" && (
                        <Button size="sm" onClick={()=>setCompleteRefundModal(o)} className="rounded-lg text-[12px]"
                          style={{background:"#fef2f2",color:"#991b1b",border:"1px solid #fca5a5"}}
                          data-testid={`refund-pending-${o.order_number}`} title="Complete this pending refund">
                          <Banknote className="h-3 w-3 mr-1"/>Refund {formatAud(o.refund_amount)} Pending
                        </Button>
                      )}
                      {o.pinnacle_issue && (
                        <Button size="sm" onClick={()=>setSelectedOrder(o)} className="rounded-lg text-[12px]"
                          style={{background:"#fef2f2",color:"#991b1b",border:"1px solid #fca5a5"}}
                          data-testid={`order-issue-${o.order_number}`} title="Pinnacle reported an issue with this order">
                          <AlertOctagon className="h-3 w-3 mr-1"/>Pinnacle Issue
                        </Button>
                      )}
                    </div>
                  </td>

                </tr>
              );
            })}
            {visible.length===0 && (
              <tr><td colSpan={8} className="p-12 text-center font-mono uppercase tracking-widest text-[11px]" style={{color:"#94a3b8"}}>
                {search||sourceFilter!=="all"?"No orders match your search":"No orders in this period"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Order detail modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{background:"rgba(0,0,0,0.45)"}} onClick={()=>setSelectedOrder(null)}
          data-testid="admin-order-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl"
            style={{border:"1px solid #dde3ef"}} onClick={e=>e.stopPropagation()}>
            {/* Modal header */}
            <div className="p-5 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl" style={{borderBottom:"1px solid #eef2f8"}}>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{color:"#94a3b8"}}>Order</div>
                <div className="font-heading text-xl font-bold" style={{color:"#0f172a"}}>#{selectedOrder.order_number}</div>
              </div>
              <div className="flex items-center gap-2">
                <span style={STATUS_STYLE[selectedOrder.status]||{}} className="text-[11px] font-mono font-bold uppercase tracking-wide px-3 py-1.5 rounded-lg">
                  {selectedOrder.status}
                </span>
                <Button variant="ghost" onClick={()=>setSelectedOrder(null)} style={{color:"#475569"}}>Close</Button>
              </div>
            </div>
            <div className="p-5 space-y-4 text-sm" style={{color:"#0f172a"}}>

              {/* ── Pinnacle order issue — part sold/deleted before order could process ── */}
              {selectedOrder.pinnacle_issue && (
                <div className="rounded-xl p-4" style={{background:"#fef2f2",border:"1px solid #fca5a5"}} data-testid="order-issue-banner">
                  <div className="flex items-center gap-2 font-heading font-bold text-[13px]" style={{color:"#991b1b"}}>
                    <AlertOctagon className="h-4 w-4"/> Pinnacle Issue
                  </div>
                  <div className="text-[13px] mt-1" style={{color:"#7f1d1d"}}>
                    {selectedOrder.pinnacle_issue_message || "Pinnacle reported a problem with this order."}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" onClick={()=>notifyIssue(selectedOrder)}
                      className="rounded-lg text-[12px]" style={{background:"#fff",color:"#991b1b",border:"1px solid #fca5a5"}}
                      data-testid="notify-issue-btn">
                      <Mail className="h-3 w-3 mr-1"/>
                      {selectedOrder.customer_notified_at ? "Notify Again" : "Notify Customer"}
                    </Button>
                    <Button size="sm" onClick={()=>setSwapModal(selectedOrder)}
                      className="rounded-lg text-[12px]" style={{background:"#e31e24",color:"#fff"}}
                      data-testid="swap-item-btn">
                      <RefreshCcw className="h-3 w-3 mr-1"/> Swap Item
                    </Button>
                  </div>
                  {selectedOrder.customer_notified_at && (
                    <div className="text-[11px] mt-2" style={{color:"#991b1b"}}>
                      Customer notified {new Date(selectedOrder.customer_notified_at).toLocaleString("en-AU")}
                    </div>
                  )}
                </div>
              )}

              {/* ── After a swap, awaiting a manual re-push to Pinnacle ── */}
              {!selectedOrder.pinnacle_issue && selectedOrder.sync_status === "pending" && selectedOrder.status_history?.some(h => h.note?.includes("Item swapped")) && (
                <div className="rounded-xl p-4 flex items-center justify-between" style={{background:"#eff6ff",border:"1px solid #93c5fd"}}>
                  <div className="text-[13px]" style={{color:"#1e40af"}}>Item swapped — ready to push to Pinnacle when you're happy with it.</div>
                  <Button size="sm" onClick={()=>pushToPinnacle(selectedOrder)}
                    className="rounded-lg text-[12px]" style={{background:"#1e5bb8",color:"#fff"}}
                    data-testid="push-pinnacle-btn">
                    <Send className="h-3 w-3 mr-1"/> Push to Pinnacle
                  </Button>
                </div>
              )}

              {/* ── Confirmed real Pinnacle work order number ── */}
              {selectedOrder.pinnacle_work_order && (
                <div className="flex items-center gap-2 text-[12px]" style={{color:"#15803d"}}>
                  <CheckCircle2 className="h-3.5 w-3.5"/> Confirmed in Pinnacle — Work Order #{selectedOrder.pinnacle_work_order}
                </div>
              )}

              {/* ── PARTS — prominent at top with inventory links ──── */}
              <div className="rounded-xl overflow-hidden" style={{border:"1px solid #1e5bb8"}}>
                <div className="px-4 py-2 flex items-center justify-between" style={{background:"#1e5bb8"}}>
                  <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-white">
                    {selectedOrder.items?.length > 1 ? `${selectedOrder.items.length} Parts` : "Part"}
                  </span>
                  <span className="text-[10px] font-mono text-white opacity-70">Click part → opens in inventory</span>
                </div>
                {selectedOrder.items?.map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3"
                    style={{
                      background: i%2===0 ? "#f0f7ff" : "#e8f1ff",
                      borderBottom: i < selectedOrder.items.length-1 ? "1px solid #bfdbfe" : "none"
                    }}>
                    <div>
                      <button onClick={() => openInInventory(item.sku)}
                        className="font-semibold text-[13px] hover:underline flex items-center gap-1.5 text-left"
                        style={{color:"#1e40af"}}>
                        {item.name || item.sku}
                        <ExternalLink className="h-3 w-3 opacity-60 shrink-0"/>
                      </button>
                      <div className="text-[10px] font-mono mt-0.5" style={{color:"#64748b"}}>SKU: {item.sku}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold" style={{color:"#0f172a"}}>{formatAud(item.price)}</div>
                      <div className="text-[10px] font-mono" style={{color:"#64748b"}}>qty {item.qty}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Fulfilment + Insurance status row */}
              <div className="flex flex-wrap gap-2">
                {selectedOrder.fulfillment_type==="click_collect" ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase px-2.5 py-1 rounded-lg"
                    style={{background:"#eff6ff",color:"#1e40af",border:"1px solid #93c5fd"}}>
                    <Store className="h-3 w-3"/> Click &amp; Collect
                    {selectedOrder.collect_by_date && (
                      <span className="ml-1 opacity-70">· by {fmtDate(selectedOrder.collect_by_date)}</span>
                    )}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase px-2.5 py-1 rounded-lg"
                    style={{background:"#f5f7fb",color:"#64748b",border:"1px solid #dde3ef"}}>
                    <Truck className="h-3 w-3"/> Home Delivery
                  </span>
                )}
                {selectedOrder.insurance ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase px-2.5 py-1 rounded-lg"
                    style={{background:"#dcfce7",color:"#15803d",border:"1px solid #86efac"}}>
                    <Shield className="h-3 w-3"/> Insured · {formatAud(selectedOrder.insurance_amount||0)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase px-2.5 py-1 rounded-lg"
                    style={{background:"#fef2f2",color:"#991b1b",border:"1px solid #fca5a5"}}>
                    <ShieldOff className="h-3 w-3"/> Not insured — transit damage not covered
                  </span>
                )}
              </div>
              {/* Customer + address */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl p-3" style={{background:"#f5f7fb",border:"1px solid #dde3ef"}}>
                  <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{color:"#94a3b8"}}>Customer</div>
                  <div className="font-semibold">{selectedOrder.customer_name}</div>
                  <div style={{color:"#64748b"}}>{selectedOrder.customer_email}</div>
                  <div style={{color:"#64748b"}}>{selectedOrder.customer_phone}</div>
                </div>
                <div className="rounded-xl p-3" style={{background:"#f5f7fb",border:"1px solid #dde3ef"}}>
                  <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{color:"#94a3b8"}}>
                    {selectedOrder.fulfillment_type==="click_collect" ? "Collection" : "Ship to"}
                  </div>
                  {selectedOrder.fulfillment_type==="click_collect" ? (
                    <>
                      <div className="font-semibold">BCP Brisbane Yard</div>
                      <div style={{color:"#64748b"}}>Collect by: {selectedOrder.collect_by_date ? fmtDate(selectedOrder.collect_by_date) : "—"}</div>
                    </>
                  ) : (
                    <>
                      <div>{selectedOrder.shipping_address?.line1}</div>
                      <div style={{color:"#64748b"}}>{selectedOrder.shipping_address?.suburb} {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postcode}</div>
                    </>
                  )}
                </div>
              </div>
              {/* Line items */}
              <div className="rounded-xl overflow-hidden" style={{border:"1px solid #eef2f8"}}>
                {selectedOrder.items.map((i)=>(
                  <div key={i.sku} className="flex justify-between p-3" style={{borderBottom:"1px solid #f0f4f8"}}>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest" style={{color:"#94a3b8"}}>{i.sku}</div>
                      <div className="font-medium">{i.name}</div>
                    </div>
                    <div className="font-mono font-medium">{i.qty} × {formatAud(i.price)}</div>
                  </div>
                ))}
              </div>
              {/* Totals */}
              <div className="space-y-1.5" style={{color:"#475569"}}>
                <div className="flex justify-between text-[13px]"><span>Subtotal</span><span>{formatAud(selectedOrder.subtotal)}</span></div>
                <div className="flex justify-between text-[13px]"><span>GST</span><span>{formatAud(selectedOrder.tax)}</span></div>
                <div className="flex justify-between text-[13px]"><span>Shipping</span><span>{selectedOrder.shipping===0?"FREE":formatAud(selectedOrder.shipping)}</span></div>
                {selectedOrder.insurance_amount>0 && (
                  <div className="flex justify-between text-[13px]" style={{color:"#16a34a"}}>
                    <span className="flex items-center gap-1"><Shield className="h-3 w-3"/>Transit insurance</span>
                    <span>+{formatAud(selectedOrder.insurance_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-heading font-bold text-[16px] pt-2" style={{borderTop:"1px solid #eef2f8",color:"#0f172a"}}>
                  <span>Total</span><span>{formatAud(selectedOrder.total)}</span>
                </div>
                {selectedOrder.credit_amount>0 && (
                  <div className="flex justify-between pt-2 mt-1" style={{borderTop:"1px solid #eef2f8",color:"#e31e24"}}>
                    <span>Credited ({selectedOrder.credit_status})</span>
                    <span>− {formatAud(selectedOrder.credit_amount)}</span>
                  </div>
                )}
              </div>
              {/* Pinnacle sync */}
              <div className="rounded-xl p-3" style={{background:"#f5f7fb",border:"1px solid #dde3ef"}}>
                <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{color:"#94a3b8"}}>Pinnacle Sync</div>
                <SyncBadge
                  status={selectedOrder.sync_status||(selectedOrder.exported_to_pinnacle?"synced":"pending")}
                  lastSyncedAt={selectedOrder.last_synced_at}
                  syncError={selectedOrder.sync_error}
                  onRetry={selectedOrder.sync_status==="failed"||selectedOrder.sync_status==="dead"?()=>retrySync(selectedOrder):undefined}
                  onForceSync={(!selectedOrder.sync_status||selectedOrder.sync_status==="pending")?()=>forceSync(selectedOrder):undefined}
                />
                {selectedOrder.external_id && (
                  <div className="text-[11px] font-mono mt-1" style={{color:"#64748b"}}>Pinnacle ref: {selectedOrder.external_id}</div>
                )}
              </div>
              {/* Payment link */}
              {selectedOrder.payment_status!=="paid" && <PayLinkPanel order={selectedOrder} onUpdated={load}/>}
              {/* Invoice actions */}
              <InvoiceActions
                order={selectedOrder}
                onDone={() => { load(); setSelectedOrder(null); }}
                adminApi={adminApi}
                toast={toast}
              />

              {/* Review request — only show after invoiced */}
              {selectedOrder.status === "invoiced" && selectedOrder.customer_email && (
                <div className="pt-1">
                  <button
                    onClick={async () => {
                      try {
                        await adminApi.post(`/admin/orders/${selectedOrder.id}/request-review`);
                        toast.success(`Review request sent to ${selectedOrder.customer_email}`);
                      } catch(e) {
                        toast.error(e?.response?.data?.detail || "Failed to send review request");
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                    style={{background:"#fffbeb", border:"1px solid #fde68a", color:"#92400e"}}>
                    ⭐ Request Google Review
                  </button>
                </div>
              )}
              {/* Status timeline */}
              {selectedOrder.status_history?.length>0 && (
                <div className="pt-3" style={{borderTop:"1px solid #eef2f8"}} data-testid="order-status-timeline">
                  <div className="text-[10px] font-mono uppercase tracking-widest mb-3 font-bold" style={{color:"#94a3b8"}}>Status Timeline</div>
                  <ol className="space-y-3">
                    {selectedOrder.status_history.map((h,i)=>(
                      <li key={i} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-2 h-2 rounded-full mt-1.5" style={{background:"#e31e24"}}/>
                          {i<selectedOrder.status_history.length-1 && <div className="w-px flex-1 mt-1" style={{background:"#eef2f8",minHeight:24}}/>}
                        </div>
                        <div className="pb-2">
                          <div className="text-sm font-semibold uppercase tracking-wide" style={{color:"#0f172a"}}>{h.status}</div>
                          <div className="text-[11px] font-mono mt-0.5" style={{color:"#94a3b8"}}>{new Date(h.at).toLocaleString("en-AU")} · {h.by||"system"}</div>
                          {h.note && <div className="text-xs mt-1" style={{color:"#64748b"}}>{h.note}</div>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {manualOpen && (
        <ManualOrderModal onClose={()=>setManualOpen(false)} onCreated={(o)=>{setManualOpen(false);load();setSelectedOrder(o);}}/>
      )}
      {shippingModal && (
        <ShippingModal
          order={shippingModal}
          onClose={() => setShippingModal(null)}
          onConfirm={(extra) => updateStatus(shippingModal, "shipped", extra)}
        />
      )}
      {refundModal && (
        <RefundChoiceModal
          order={refundModal}
          onClose={() => setRefundModal(null)}
          onDone={() => { setRefundModal(null); load(); }}
        />
      )}
      {completeRefundModal && (
        <CompleteRefundModal
          order={completeRefundModal}
          onClose={() => setCompleteRefundModal(null)}
          onDone={() => { setCompleteRefundModal(null); load(); }}
        />
      )}
      {swapModal && (
        <SwapItemModal
          order={swapModal}
          onClose={() => setSwapModal(null)}
          onDone={() => { setSwapModal(null); setSelectedOrder(null); load(); }}
        />
      )}
    </div>
  );
}

/* ─── PayLinkButton ───────────────────────────────────────────────── */
function PayLinkButton({order,onUpdated}){
  const [busy,setBusy]=useState(false);
  const [copied,setCopied]=useState(false);
  const link=order.payment_link;
  const generate=async(e)=>{e.stopPropagation();setBusy(true);try{const{data}=await adminApi.post(`/admin/orders/${order.id}/payment-link`,{});await navigator.clipboard?.writeText(data.url);setCopied(true);toast.success("Payment link copied");onUpdated?.();setTimeout(()=>setCopied(false),2500);}catch(e){toast.error(e.response?.data?.detail||"Failed");}finally{setBusy(false);}};
  const copyExisting=async(e)=>{e.stopPropagation();await navigator.clipboard?.writeText(link.url);setCopied(true);toast.success("Payment link copied");setTimeout(()=>setCopied(false),2500);};
  if(link?.url)return(<Button size="sm" onClick={copyExisting} className="rounded-lg text-[12px]" style={{background:"#dcfce7",color:"#15803d",border:"1px solid #86efac"}} data-testid={`pay-link-copy-${order.order_number}`} title="Copy payment link">{copied?<Check className="h-3 w-3 mr-1"/>:<Copy className="h-3 w-3 mr-1"/>}Pay link</Button>);
  return(<Button size="sm" onClick={generate} disabled={busy} className="rounded-lg text-[12px]" style={{background:"#dbeafe",color:"#1e40af",border:"1px solid #93c5fd"}} data-testid={`pay-link-create-${order.order_number}`}><Link2 className="h-3 w-3 mr-1"/>{busy?"…":"Pay link"}</Button>);
}

/* ─── PayLinkPanel ────────────────────────────────────────────────── */
function PayLinkPanel({order,onUpdated}){
  const [link,setLink]=useState(order.payment_link||null);
  const [busy,setBusy]=useState(false);
  const [copied,setCopied]=useState(false);
  const generate=async()=>{setBusy(true);try{const{data}=await adminApi.post(`/admin/orders/${order.id}/payment-link`,{});setLink(data);onUpdated?.();}catch(e){toast.error(e.response?.data?.detail||"Failed");}finally{setBusy(false);}};
  const copy=async()=>{if(!link?.url)return;await navigator.clipboard?.writeText(link.url);setCopied(true);toast.success("Copied");setTimeout(()=>setCopied(false),2500);};
  return(
    <div className="rounded-xl p-4 space-y-3" style={{background:"#eff6ff",border:"1px solid #93c5fd"}} data-testid="pay-link-panel">
      <div className="flex items-start gap-3"><Link2 className="h-5 w-5 mt-0.5" style={{color:"#1e5bb8"}}/><div className="flex-1"><div className="font-heading text-sm font-bold uppercase" style={{color:"#0f172a"}}>Stripe Payment Link</div><div className="text-xs mt-1" style={{color:"#475569"}}>Sends customer to Stripe checkout. Order auto-updates to paid.</div></div></div>
      {link?.url?(
        <><div className="flex items-center gap-2 rounded-lg p-2" style={{background:"#dbeafe"}}><code className="flex-1 text-[11px] font-mono truncate" style={{color:"#1e40af"}}>{link.url}</code><button onClick={copy} className="px-2 h-7 rounded-lg" style={{background:"rgba(30,91,184,0.15)",color:"#1e40af"}}>{copied?<Check className="h-3 w-3"/>:<Copy className="h-3 w-3"/>}</button><a href={link.url} target="_blank" rel="noreferrer" className="px-2 h-7 rounded-lg flex items-center" style={{background:"rgba(30,91,184,0.15)",color:"#1e40af"}}><ExternalLink className="h-3 w-3"/></a></div>
        <div className="flex justify-between"><div className="text-[11px] font-mono" style={{color:"#64748b"}}>{new Date(link.created_at).toLocaleString("en-AU")} · {formatAud(link.amount)}</div><Button size="sm" onClick={generate} disabled={busy} className="rounded-lg text-[12px]" style={{background:"#dbeafe",color:"#1e40af",border:"1px solid #93c5fd"}}><RefreshCcw className="h-3 w-3 mr-1"/>{busy?"…":"Regen"}</Button></div></>
      ):(
        <Button onClick={generate} disabled={busy} className="w-full text-white font-bold uppercase tracking-wide rounded-xl h-10" style={{background:"#1e5bb8"}}><Link2 className="h-4 w-4 mr-2"/>{busy?"Generating…":`Generate · ${formatAud(order.total)}`}</Button>
      )}
      {link?.url && <SmsHint order={order}/>}
    </div>
  );
}

/* ─── SmsHint ─────────────────────────────────────────────────────── */
function SmsHint({order}){
  const [sending,setSending]=useState(false);
  const trySend=async()=>{if(!order.customer_phone){toast.error("No phone on file");return;}setSending(true);try{await adminApi.post(`/admin/orders/${order.id}/send-payment-sms`,{phone:order.customer_phone});toast.success("SMS sent");}catch(e){const msg=e.response?.data?.detail||"SMS failed";toast.message(msg,{duration:8000,icon:"📱"});}finally{setSending(false);}};
  return(<div className="rounded-lg p-3 flex items-start gap-2 border-dashed" style={{background:"#fefce8",border:"1px dashed #fde047"}} data-testid="sms-hint"><MessageSquare className="h-4 w-4 mt-0.5 shrink-0" style={{color:"#a16207"}}/><div className="flex-1 text-[11px]" style={{color:"#78350f"}}><b>Coming soon:</b> one-click SMS via Twilio. Add <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, <code>TWILIO_FROM</code> to <code>.env</code>.</div><Button size="sm" onClick={trySend} disabled={sending||!order.customer_phone} className="rounded-lg whitespace-nowrap text-[11px]" style={{background:"#fef3c7",color:"#92400e",border:"1px solid #fde68a"}}><Phone className="h-3 w-3 mr-1"/>{sending?"…":"Send SMS"}</Button></div>);
}

/* ─── ManualOrderModal ─────────────────────────────────────────────── */
const EMPTY_LINE={product_id:null,sku:"",name:"",price:0,qty:1};

function ManualOrderModal({onClose,onCreated}){
  const [source,      setSource]      = useState("walkin");
  const [fulfillType, setFulfillType] = useState("delivery");
  const [insurance,   setInsurance]   = useState(false);
  const [customer,    setCustomer]    = useState({name:"",email:"",phone:"",line1:"",suburb:"",state:"QLD",postcode:""});
  const [lines,       setLines]       = useState([{...EMPTY_LINE}]);
  const [payment,     setPayment]     = useState("walkin_cash");
  const [shipping,    setShipping]    = useState(0);
  const [discount,    setDiscount]    = useState(0);
  const [notes,       setNotes]       = useState("");
  const [status,      setStatus]      = useState("paid");
  const [genLink,     setGenLink]     = useState(false);
  const [busy,        setBusy]        = useState(false);

  const subtotal      = lines.reduce((s,l)=>s+Number(l.price||0)*Number(l.qty||0),0)-Number(discount||0);
  const base          = Math.max(subtotal,0)+(fulfillType==="delivery"?Number(shipping||0):0);
  const insuranceAmt  = insurance ? Math.max(Math.round(base*0.10*100)/100,3) : 0;
  const grandTotal    = Math.round((base+insuranceAmt)*100)/100;

  const addLine    = ()=>setLines(arr=>[...arr,{...EMPTY_LINE}]);
  const removeLine = (i)=>setLines(arr=>arr.filter((_,j)=>j!==i));
  const updateLine = (i,patch)=>setLines(arr=>arr.map((l,j)=>j===i?{...l,...patch}:l));

  const submit=async()=>{
    const validLines=lines.filter(l=>l.sku&&l.price>0);
    if(validLines.length===0){toast.error("Add at least one item (SKU + price)");return;}
    if(payment==="stripe_link"&&!customer.email){toast.error("Customer email required for Stripe link");return;}
    setBusy(true);
    try{
      const body={
        source,
        customer_name:customer.name,
        customer_phone:customer.phone,
        notes,
        items:validLines,
        payment_method:payment,
        status,
        shipping:fulfillType==="delivery"?Number(shipping||0):0,
        discount:Number(discount||0),
        generate_payment_link:genLink||payment==="stripe_link",
        insurance,
        insurance_amount:insuranceAmt,
        fulfillment_type:fulfillType,
      };
      if(customer.email)body.customer_email=customer.email;
      if(customer.line1&&fulfillType==="delivery")body.shipping_address={line1:customer.line1,suburb:customer.suburb,state:customer.state,postcode:customer.postcode};
      else if(fulfillType==="click_collect")body.shipping_address={line1:"Click & Collect — BCP Brisbane",suburb:"Brisbane",state:"QLD",postcode:"4000"};
      const{data}=await adminApi.post("/admin/orders/manual",body);
      toast.success(`Order ${data.order.order_number} created`);
      if(data.order.payment_link?.url){navigator.clipboard?.writeText(data.order.payment_link.url);toast.success("Payment link copied");}
      onCreated(data.order);
    }catch(e){toast.error(e.response?.data?.detail||"Failed to create order");}
    finally{setBusy(false);}
  };

  const inp={background:"#ffffff",borderColor:"#dde3ef",color:"#0f172a"};
  const btn=(active)=>active
    ?{background:"#e31e24",border:"1px solid #e31e24",color:"#ffffff"}
    :{background:"#f5f7fb",border:"1px solid #dde3ef",color:"#475569"};

  return(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:"rgba(0,0,0,0.5)"}} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" style={{border:"1px solid #dde3ef"}} onClick={e=>e.stopPropagation()} data-testid="manual-order-modal">
        {/* Header */}
        <div className="p-5 flex items-start justify-between sticky top-0 bg-white rounded-t-2xl z-10" style={{borderBottom:"1px solid #eef2f8"}}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{color:"#94a3b8"}}>New order</div>
            <div className="font-heading text-2xl font-bold" style={{color:"#0f172a"}}>Create manual order</div>
            <div className="text-xs mt-1" style={{color:"#64748b"}}>Walk-in, phone, eBay or trade. Queued for Pinnacle sync.</div>
          </div>
          <Button variant="ghost" onClick={onClose} style={{color:"#475569"}}><X className="h-4 w-4"/></Button>
        </div>

        <div className="p-5 space-y-5">
          {/* Source */}
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Source</Label>
            <div className="mt-2 grid grid-cols-5 gap-2">
              {[{v:"walkin",label:"Walk-in",I:Store},{v:"phone",label:"Phone",I:Phone},{v:"ebay",label:"eBay",I:ShoppingCart},{v:"trade",label:"Trade",I:Building2},{v:"website",label:"Website",I:Globe}].map(({v,label,I})=>(
                <button key={v} type="button" onClick={()=>setSource(v)} data-testid={`source-${v}`}
                  className="py-2 rounded-xl flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-widest" style={btn(source===v)}>
                  <I className="h-4 w-4"/>{label}
                </button>
              ))}
            </div>
          </div>

          {/* Fulfillment type */}
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Fulfilment Type</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {[{v:"delivery",label:"Home Delivery",I:Truck},{v:"click_collect",label:"Click & Collect",I:Store}].map(({v,label,I})=>(
                <button key={v} type="button" onClick={()=>setFulfillType(v)}
                  className="py-2.5 rounded-xl flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-widest"
                  style={fulfillType===v?{background:"#1e5bb8",border:"1px solid #1e5bb8",color:"#ffffff"}:{background:"#f5f7fb",border:"1px solid #dde3ef",color:"#475569"}}>
                  <I className="h-4 w-4"/>{label}
                </button>
              ))}
            </div>
            {fulfillType==="click_collect" && (
              <div className="mt-2 p-2.5 rounded-xl text-[11px]" style={{background:"#fef3c7",border:"1px solid #fde68a",color:"#92400e"}}>
                ⚠ Part held 7 days. Auto-cancelled after — no refunds. Collect by: {collectByDate()}
              </div>
            )}
          </div>

          {/* Customer */}
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Customer name</Label>
              <Input value={customer.name} onChange={e=>setCustomer({...customer,name:e.target.value})} placeholder={source==="walkin"?"Walk-in customer":"Full name"} className="mt-1 rounded-xl" style={inp} data-testid="manual-customer-name"/></div>
            <div><Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Phone</Label>
              <Input value={customer.phone} onChange={e=>setCustomer({...customer,phone:e.target.value})} placeholder="0400 000 000" className="mt-1 rounded-xl" style={inp} data-testid="manual-customer-phone"/></div>
            <div className="md:col-span-2"><Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Email {payment==="stripe_link"&&<span style={{color:"#e31e24"}}>*</span>}</Label>
              <Input type="email" value={customer.email} onChange={e=>setCustomer({...customer,email:e.target.value})} placeholder="customer@example.com" className="mt-1 rounded-xl" style={inp} data-testid="manual-customer-email"/></div>
          </div>

          {/* Line items */}
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Line items</Label>
            <div className="mt-2 rounded-xl overflow-hidden" style={{border:"1px solid #dde3ef"}}>
              {lines.map((l,i)=><LineRow key={i} line={l} onChange={patch=>updateLine(i,patch)} onRemove={lines.length>1?()=>removeLine(i):null} index={i}/>)}
            </div>
            <button type="button" onClick={addLine} className="mt-2 text-xs font-mono uppercase tracking-widest hover:underline" style={{color:"#1e5bb8"}} data-testid="manual-add-line">
              <Plus className="h-3 w-3 inline"/> Add line
            </button>
          </div>

          {/* Totals */}
          <div className="grid md:grid-cols-3 gap-3">
            {fulfillType==="delivery" && (
              <div><Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Shipping (AUD)</Label>
                <Input type="number" step="0.01" value={shipping} onChange={e=>setShipping(e.target.value)} className="mt-1 rounded-xl" style={inp} data-testid="manual-shipping"/></div>
            )}
            <div><Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Discount (AUD)</Label>
              <Input type="number" step="0.01" value={discount} onChange={e=>setDiscount(e.target.value)} className="mt-1 rounded-xl" style={inp} data-testid="manual-discount"/></div>
            <div><Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Grand Total</Label>
              <div className="mt-1 h-10 px-3 rounded-xl flex items-center font-heading text-lg font-bold" style={{background:"#f5f7fb",border:"1px solid #dde3ef",color:"#0f172a"}} data-testid="manual-total">{formatAud(grandTotal)}</div></div>
          </div>

          {/* Transit insurance */}
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Transit Insurance</Label>
            <button type="button" onClick={()=>setInsurance(!insurance)}
              className="mt-2 w-full flex items-center gap-3 p-3 rounded-xl transition-all"
              style={insurance?{background:"#dcfce7",border:"1px solid #86efac"}:{background:"#f5f7fb",border:"1px solid #dde3ef"}}>
              {insurance
                ?<Shield className="h-4 w-4 shrink-0" style={{color:"#16a34a"}}/>
                :<ShieldOff className="h-4 w-4 shrink-0" style={{color:"#94a3b8"}}/>
              }
              <div className="flex-1 text-left">
                <div className="text-[12px] font-semibold" style={{color:"#0f172a"}}>{insurance?"Transit insurance added":"Add transit insurance"}</div>
                <div className="text-[11px]" style={{color:"#64748b"}}>
                  {insurance?`+${formatAud(insuranceAmt)} included in total`:"10% of total (min $3) · covers transit damage claims"}
                </div>
              </div>
              {insurance&&<span className="text-[12px] font-mono font-bold" style={{color:"#16a34a"}}>+{formatAud(insuranceAmt)}</span>}
            </button>
            {!insurance && (
              <div className="mt-2 text-[11px]" style={{color:"#94a3b8"}}>
                Without insurance: credits for transit damage will be rejected. Wrong/faulty parts still covered after verification.
              </div>
            )}
          </div>

          {/* Payment */}
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Payment method</Label>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2">
              {[{v:"walkin_cash",label:"Cash"},{v:"walkin_card",label:"Card"},{v:"bank_transfer",label:"Bank"},{v:"cod",label:"COD"},{v:"stripe_link",label:"Stripe"}].map(m=>(
                <button key={m.v} type="button" onClick={()=>setPayment(m.v)} data-testid={`payment-${m.v}`}
                  className="py-2 rounded-xl text-[11px] font-mono uppercase tracking-widest" style={btn(payment===m.v)}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status + notes */}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Initial status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1 rounded-xl" style={inp} data-testid="manual-status"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Paid / Complete</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="packed">Packed</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="new">New (draft)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{color:"#64748b"}}>Notes</Label>
              <Input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Internal notes" className="mt-1 rounded-xl" style={inp} data-testid="manual-notes"/>
            </div>
          </div>

          {payment!=="stripe_link" && (
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{color:"#475569"}}>
              <input type="checkbox" checked={genLink} onChange={e=>setGenLink(e.target.checked)} data-testid="manual-genlink"/>
              Also generate a Stripe payment link
            </label>
          )}

          <div className="flex items-center gap-2 p-2.5 rounded-xl text-[11px]" style={{background:"#f5f7fb",border:"1px solid #dde3ef",color:"#475569"}}>
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" style={{color:"#d97706"}}/>
            Offline-safe: saves locally and queues for Pinnacle sync regardless of connection status.
          </div>

          <Button onClick={submit} disabled={busy} className="w-full h-12 text-white font-bold uppercase tracking-wide rounded-xl text-[14px]" style={{background:"#e31e24"}} data-testid="manual-submit-btn">
            {busy?"Creating order…":`Create order · ${formatAud(grandTotal)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── LineRow ─────────────────────────────────────────────────────── */
function LineRow({line,onChange,onRemove,index}){
  const [suggestions,setSuggestions]=useState([]);
  const [open,setOpen]=useState(false);
  const search=async(q)=>{onChange({sku:q});if(!q||q.length<2){setSuggestions([]);return;}try{const{data}=await adminApi.get(`/admin/products?q=${encodeURIComponent(q)}&page_size=8`);setSuggestions(data.items||[]);setOpen(true);}catch{setSuggestions([]);}};
  const pick=(p)=>{onChange({product_id:p.id,sku:p.sku,name:p.name,price:Number(p.price)});setSuggestions([]);setOpen(false);};
  const inp={background:"#ffffff",borderColor:"#dde3ef",color:"#0f172a",height:32};
  return(
    <div className="p-2 flex items-center gap-2 relative" style={{borderBottom:"1px solid #f0f4f8"}}>
      <div className="flex-1 min-w-0">
        <div className="relative">
          <Input value={line.sku} onChange={e=>search(e.target.value)} onFocus={()=>line.sku&&setOpen(true)} onBlur={()=>setTimeout(()=>setOpen(false),150)} placeholder="SKU or search…" className="rounded-lg text-xs" style={inp} data-testid={`manual-line-sku-${index}`}/>
          {open&&suggestions.length>0&&(
            <div className="absolute z-20 top-full left-0 right-0 mt-1 rounded-xl max-h-48 overflow-y-auto shadow-xl bg-white" style={{border:"1px solid #dde3ef"}}>
              {suggestions.map(p=>(
                <button key={p.id} onClick={()=>pick(p)} type="button" className="w-full px-3 py-2 text-left hover:bg-[#f5f7fb] text-[11px] flex items-center justify-between gap-2">
                  <div className="min-w-0"><div className="font-medium truncate" style={{color:"#0f172a"}}>{p.name}</div><div className="font-mono" style={{color:"#94a3b8",fontSize:10}}>{p.sku} · stock {p.stock}</div></div>
                  <div className="font-mono font-medium shrink-0" style={{color:"#0f172a"}}>{formatAud(p.price)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <Input value={line.name} onChange={e=>onChange({name:e.target.value})} placeholder="Description" className="mt-1 rounded-lg text-xs" style={inp}/>
      </div>
      <Input type="number" value={line.price} min="0" step="0.01" onChange={e=>onChange({price:Number(e.target.value)})} className="w-24 rounded-lg text-xs" style={inp} data-testid={`manual-line-price-${index}`}/>
      <Input type="number" value={line.qty} min="1" onChange={e=>onChange({qty:Number(e.target.value)})} className="w-16 rounded-lg text-xs" style={inp} data-testid={`manual-line-qty-${index}`}/>
      {onRemove&&<Button size="sm" variant="ghost" onClick={onRemove} style={{color:"#e31e24"}}><Trash2 className="h-3 w-3"/></Button>}
    </div>
  );
}
/* ─── Refund method fields — shared between "cancel now" and "complete later" ─── */
const REFUND_METHODS = [
  { value: "stripe",        label: "Stripe (manual refund)" },
  { value: "payid",         label: "PayID" },
  { value: "cash",          label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

function RefundMethodFields({ method, details, setDetails }) {
  if (method === "payid") {
    return (
      <Input value={details.payid || ""} onChange={e => setDetails(d => ({ ...d, payid: e.target.value }))}
        placeholder="PayID used (phone, email, or ABN)" className="rounded-lg text-xs" data-testid="refund-payid" />
    );
  }
  if (method === "bank_transfer") {
    return (
      <div className="space-y-2">
        <Input value={details.account_name || ""} onChange={e => setDetails(d => ({ ...d, account_name: e.target.value }))}
          placeholder="Account name" className="rounded-lg text-xs" />
        <div className="grid grid-cols-2 gap-2">
          <Input value={details.bsb || ""} onChange={e => setDetails(d => ({ ...d, bsb: e.target.value }))}
            placeholder="BSB" className="rounded-lg text-xs" />
          <Input value={details.account_number || ""} onChange={e => setDetails(d => ({ ...d, account_number: e.target.value }))}
            placeholder="Account number" className="rounded-lg text-xs" />
        </div>
      </div>
    );
  }
  if (method === "cash") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Input value={details.given_to || ""} onChange={e => setDetails(d => ({ ...d, given_to: e.target.value }))}
          placeholder="Given to (name)" className="rounded-lg text-xs" />
        <Input type="date" value={details.date_given || ""} onChange={e => setDetails(d => ({ ...d, date_given: e.target.value }))}
          className="rounded-lg text-xs" />
      </div>
    );
  }
  if (method === "stripe") {
    return (
      <Input value={details.reference || ""} onChange={e => setDetails(d => ({ ...d, reference: e.target.value }))}
        placeholder="Reference / note (optional)" className="rounded-lg text-xs" />
    );
  }
  return null;
}

/* ─── Refund choice — shown when cancelling a paid order ─────────────── */
function RefundChoiceModal({ order, onClose, onDone }) {
  const [choice, setChoice] = useState(null); // "now" | "later"
  const [amount, setAmount] = useState(order.total);
  const [method, setMethod] = useState("stripe");
  const [details, setDetails] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/orders/${order.id}/cancel-with-refund`, {
        refund_choice: choice,
        amount: Number(amount) || 0,
        method: choice === "now" ? method : null,
        details: choice === "now" ? details : null,
        notes,
      });
      toast.success(choice === "now" ? "Order cancelled and refund recorded" : "Order cancelled — refund flagged for later");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel this order");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" style={{ border: "1px solid #dde3ef" }} onClick={e => e.stopPropagation()}>
        <div className="p-5" style={{ borderBottom: "1px solid #eef2f8" }}>
          <div className="font-heading text-lg font-bold" style={{ color: "#0f172a" }}>This order was paid</div>
          <div className="text-[12px] mt-1" style={{ color: "#64748b" }}>
            {order.order_number} · {formatAud(order.total)} paid by {order.customer_name}
          </div>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div className="flex gap-2">
            <button onClick={() => setChoice("now")}
              className="flex-1 h-11 rounded-xl text-[13px] font-semibold"
              style={{ background: choice === "now" ? "#1e5bb8" : "#f5f7fb", color: choice === "now" ? "#fff" : "#64748b" }}
              data-testid="refund-choice-now">
              Refund Now
            </button>
            <button onClick={() => setChoice("later")}
              className="flex-1 h-11 rounded-xl text-[13px] font-semibold"
              style={{ background: choice === "later" ? "#1e5bb8" : "#f5f7fb", color: choice === "later" ? "#fff" : "#64748b" }}
              data-testid="refund-choice-later">
              Handle Later
            </button>
          </div>

          {choice && (
            <>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: "#94a3b8" }}>Refund Amount</div>
                <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                  className="rounded-lg text-[13px] font-mono" />
              </div>

              {choice === "now" && (
                <>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: "#94a3b8" }}>Refund Method</div>
                    <select value={method} onChange={e => { setMethod(e.target.value); setDetails({}); }}
                      className="w-full h-10 px-3 rounded-lg text-[13px] outline-none" style={{ border: "1px solid #dde3ef" }}
                      data-testid="refund-method-select">
                      {REFUND_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <RefundMethodFields method={method} details={details} setDetails={setDetails} />
                </>
              )}

              {choice === "later" && (
                <div className="text-[12px] p-3 rounded-lg" style={{ background: "#fffbeb", color: "#92400e" }}>
                  This order will be cancelled now. The refund will show as pending on the Dashboard until you come back and complete it.
                </div>
              )}

              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" className="rounded-lg text-[13px]" />
            </>
          )}
        </div>

        <div className="p-5 flex gap-2" style={{ borderTop: "1px solid #eef2f8" }}>
          <button onClick={onClose} className="flex-1 h-11 rounded-xl text-[13px] font-semibold"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            Back
          </button>
          <button onClick={submit} disabled={!choice || busy}
            className="flex-1 h-11 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide disabled:opacity-50"
            style={{ background: "#e31e24" }} data-testid="refund-confirm-submit">
            {busy ? "Saving…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Complete a previously-deferred refund ───────────────────────────── */
function CompleteRefundModal({ order, onClose, onDone }) {
  const [amount, setAmount] = useState(order.refund_amount || order.total);
  const [method, setMethod] = useState("stripe");
  const [details, setDetails] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/orders/${order.id}/complete-refund`, {
        amount: Number(amount) || 0, method, details, notes,
      });
      toast.success("Refund completed");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete this refund");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" style={{ border: "1px solid #dde3ef" }} onClick={e => e.stopPropagation()}>
        <div className="p-5" style={{ borderBottom: "1px solid #eef2f8" }}>
          <div className="font-heading text-lg font-bold" style={{ color: "#0f172a" }}>Complete Refund</div>
          <div className="text-[12px] mt-1" style={{ color: "#64748b" }}>
            {order.order_number} · {order.customer_name}
          </div>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: "#94a3b8" }}>Refund Amount</div>
            <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
              className="rounded-lg text-[13px] font-mono" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: "#94a3b8" }}>Refund Method</div>
            <select value={method} onChange={e => { setMethod(e.target.value); setDetails({}); }}
              className="w-full h-10 px-3 rounded-lg text-[13px] outline-none" style={{ border: "1px solid #dde3ef" }}>
              {REFUND_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <RefundMethodFields method={method} details={details} setDetails={setDetails} />
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" className="rounded-lg text-[13px]" />
        </div>

        <div className="p-5 flex gap-2" style={{ borderTop: "1px solid #eef2f8" }}>
          <button onClick={onClose} className="flex-1 h-11 rounded-xl text-[13px] font-semibold"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
            className="flex-1 h-11 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide disabled:opacity-50"
            style={{ background: "#e31e24" }} data-testid="complete-refund-submit">
            {busy ? "Saving…" : "Mark Refund Complete"}
          </button>
        </div>
      </div>
    </div>
  );
}
/* ─── Swap Item — replace a sold/deleted part with an available one ──── */
function SwapItemModal({ order, onClose, onDone }) {
  const [loading, setLoading]         = useState(true);
  const [originalItem, setOriginalItem] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [note, setNote]               = useState("");
  const [swapping, setSwapping]       = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await adminApi.get(`/admin/orders/${order.id}/suggest-swap`);
        setOriginalItem(data.original_item);
        setSuggestions(data.suggestions || []);
        setNote(data.note || "");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load swap suggestions");
      } finally { setLoading(false); }
    })();
  }, [order.id]);

  const doSwap = async (product) => {
    if (!window.confirm(`Swap in "${product.name}" for this order? This updates the order total and clears the Pinnacle issue — you'll push it to Pinnacle manually afterward.`)) return;
    setSwapping(true);
    try {
      await adminApi.post(`/admin/orders/${order.id}/swap-item`, { product_id: product.id });
      toast.success("Item swapped — ready to push to Pinnacle when you're happy with it");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not swap this item");
    } finally { setSwapping(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{background:"rgba(0,0,0,0.6)"}} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl" style={{border:"1px solid #dde3ef"}} onClick={e=>e.stopPropagation()}>
        <div className="p-5" style={{borderBottom:"1px solid #eef2f8"}}>
          <div className="font-heading text-lg font-bold" style={{color:"#0f172a"}}>Swap Item</div>
          <div className="text-[12px] mt-1" style={{color:"#64748b"}}>{order.order_number} · {order.customer_name}</div>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 rounded-full animate-spin mx-auto" style={{borderColor:"#1e5bb8",borderTopColor:"transparent"}}/>
            </div>
          ) : (
            <>
              {originalItem && (
                <div className="p-3 rounded-xl" style={{background:"#fef2f2",border:"1px solid #fca5a5"}}>
                  <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{color:"#991b1b"}}>Currently on order (sold/deleted)</div>
                  <div className="font-semibold text-[13px]" style={{color:"#0f172a"}}>{originalItem.name}</div>
                  <div className="text-[11px]" style={{color:"#64748b"}}>SKU {originalItem.sku} · {formatAud(originalItem.price)}</div>
                </div>
              )}

              {note && (
                <div className="text-[12px] p-3 rounded-xl" style={{background:"#fffbeb",color:"#92400e"}}>{note}</div>
              )}

              {suggestions.length > 0 ? (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{color:"#94a3b8"}}>Available replacements</div>
                  <div className="space-y-2">
                    {suggestions.map(p => (
                      <button key={p.id} onClick={()=>doSwap(p)} disabled={swapping}
                        className="w-full text-left p-3 rounded-xl flex items-center justify-between hover:shadow-sm transition-all"
                        style={{background:"#f8fafc",border:"1px solid #eef2f8"}}
                        data-testid={`swap-option-${p.sku}`}>
                        <div>
                          <div className="font-semibold text-[13px]" style={{color:"#0f172a"}}>{p.name}</div>
                          <div className="text-[11px]" style={{color:"#94a3b8"}}>
                            {[p.vehicle_year, p.vehicle_make, p.vehicle_model].filter(Boolean).join(" ")} · SKU {p.sku} · {p.stock} in stock
                          </div>
                        </div>
                        <div className="font-mono font-bold text-[14px] shrink-0" style={{color:"#0f172a"}}>{formatAud(p.price)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : !note && (
                <div className="text-center py-6 text-[12px]" style={{color:"#94a3b8"}}>No matching in-stock parts found.</div>
              )}
            </>
          )}
        </div>

        <div className="p-5" style={{borderTop:"1px solid #eef2f8"}}>
          <button onClick={onClose} className="w-full h-10 rounded-xl text-[13px] font-semibold"
            style={{background:"#f5f7fb",border:"1px solid #dde3ef",color:"#475569"}}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}